import { Component, ElementRef, AfterViewInit, ViewChild, OnDestroy } from '@angular/core'
import createScatterplot from 'regl-scatterplot'
import { fromEvent, Observable, Subject, takeUntil, debounceTime, filter, switchMap, from, EMPTY } from 'rxjs'
import * as d3 from 'd3'
import { Store } from '@ngrx/store'
import { selectionActions } from '../../../state/selection/selection.actions'
import {
    selectEditableSelectionBuckets,
    selectFreeSelectionColor,
    selectHierarchicalSelectionColor,
    selectDisplayedOnScatterPlot,
} from '../../../state/selection/selection.selectors'
import { SelectionBucket } from '../../../state/selection/selection.state'
import { Actions, ofType } from '@ngrx/effects'
import {
    plotData$,
    PlotData,
    resetPlotData,
    plotFilterData$,
    getDisplayPlotData,
    hasDisplayPlotData,
    uniquePlotPoints,
} from '../../../state/plot-data/plot-data'
import {
    selectIsLoadingPlotData,
    selectPointSize,
    selectPointOpacity,
    selectLassoType,
} from '../../../state/plot-data/plot-data.selectors'
import { userActions } from '../../../state/user/user.actions'
import { Color, ColorMap, ScatterplotMethodOptions } from 'regl-scatterplot/dist/types'
import { legendColor } from 'd3-svg-legend'
import { plotDataActions } from '../../../state/plot-data/plot-data.actions'
import { selectHasMarkers } from '../../../state/case/case.selectors'
@Component({
    selector: 'app-scatter-plot',
    templateUrl: './scatter-plot.component.html',
    styleUrls: ['./scatter-plot.component.scss'],
})
export class ScatterPlotComponent implements AfterViewInit, OnDestroy {
    @ViewChild('plotCanvas') plotCanvas: ElementRef<HTMLCanvasElement>
    @ViewChild('xAxis') xAxisContainer: ElementRef<SVGGElement>
    @ViewChild('yAxis') yAxisContainer: ElementRef<SVGGElement>
    @ViewChild('colorAxis') colorAxisContainer: ElementRef<SVGGElement>

    readonly isLoadingData$: Observable<boolean>
    readonly pointSize$: Observable<number>
    private pointSize: number
    readonly pointOpacity$: Observable<number>
    private pointOpacity: number
    readonly lassoType$: Observable<string>
    private lassoType: string

    private plot: ScatterPlot | null = null
    private pendingPlotData: PlotData | null = null
    private latestSelectionBuckets: SelectionBucket[] = []
    private segmentIds = new Uint32Array()
    private segSources: string[] = []
    /** Selectable points as `${segSource}:${segmentId}` keys when hierarchy gating is active. */
    private filterPoints: Set<string> | null = null

    private readonly xScale = d3.scaleLinear().domain([-1, 1])
    private readonly yScale = d3.scaleLinear().domain([-1, 1])
    private readonly zScale = d3
        .scaleSequential()
        .domain([0, 1])
        .interpolator(d3.interpolateViridis)

    private readonly xAxis = d3.axisBottom(this.xScale)
    private readonly yAxis = d3.axisRight(this.yScale)
    // https://d3-legend.susielu.com/#color-doc
    private readonly zLegend = legendColor()
        .cells(5)
        .shape('rect')
        .shapeWidth(10)
        .shapeHeight(10)
        .labelOffset(5)
        .labelFormat(d3.format(',.2r'))
        .shapePadding(5)
        .scale(this.zScale)
    private readonly defaultPointColorStrings = ['#575762', '#d5d5d5', '#a1a1a1']
    private pointColorStrings = this.defaultPointColorStrings.map(colorString =>
        d3.color(colorString)
    )
    /** When true, color index 0 means "not in any displayed selection" and must stay gray. */
    private colorBySelectionMembership = false
    private annotationColor: string | undefined
    private annotationColorActive: string
    private annotationColorUnsaved: string | undefined

    private readonly resizeObserver = new ResizeObserver(() => {
        this.onResize()
        this.ensurePlotReadyAndDraw()
    })

    private lassoLongPressTime: number = 700
    private touchTimeout: number | null = null
    private lastPinchDistance: number | null = null
    private touchStartTime: number | null = null
    private touchMoveAllowed: boolean | null = null

    private readonly destroy$ = new Subject<void>()
    private readonly selectionUpdate$ = new Subject<number[]>()
    /** Bumped on each draw/clear so an in-flight huge draw can bail when superseded. */
    private drawGeneration = 0

    constructor(
        private readonly store: Store,
        private readonly actions$: Actions
    ) {
        this.selectionUpdate$
            .pipe(debounceTime(100), takeUntil(this.destroy$))
            .subscribe(pointIdxs => {
                const bySegSource = new Map<string, number[]>()
                for (const idx of pointIdxs) {
                    const segId = this.segmentIds[idx]
                    const segSource = this.segSources[idx]
                    if (!segSource) continue
                    if (!this.pointMatchesFilter(idx)) continue
                    const list = bySegSource.get(segSource) ?? []
                    list.push(segId)
                    bySegSource.set(segSource, list)
                }
                for (const [segSource, points] of bySegSource) {
                    this.store.dispatch(selectionActions.updateSelection({ segSource, points }))
                }
            })

        // switchMap (not concatMap): deselect/reset must not wait for an in-flight multi-million
        // point draw — that made the UI freeze for tens of seconds after clearing the last map.
        plotData$
            .pipe(
                takeUntil(this.destroy$),
                switchMap(data => {
                    const gen = ++this.drawGeneration
                    if (!data.domainX || !data.domainY || data.points.length === 0) {
                        this.clearPlotDisplay()
                        this.store.dispatch(plotDataActions.plotDrawn())
                        return EMPTY
                    }
                    return from(this.drawPlot(data, gen))
                })
            )
            .subscribe()
        plotFilterData$.pipe(takeUntil(this.destroy$)).subscribe(filterPoints => {
            this.setFilterPoints(filterPoints)
        })

        this.store
            .select(selectEditableSelectionBuckets)
            .pipe(takeUntil(this.destroy$))
            .subscribe(buckets => {
                this.latestSelectionBuckets = buckets
                this.applySelectionHighlight(buckets)
            })

        this.store
            .select(selectFreeSelectionColor)
            .pipe(takeUntil(this.destroy$))
            .subscribe(c => {
                this.annotationColorActive = c!
                this.plot?.set({
                    pointColorActive: this.pointColorStrings.map((color, i) => {
                        return i === 0 ? (c as Color) : color!.brighter().formatHex()
                    }) as any,
                })
            })

        this.store
            .select(selectHierarchicalSelectionColor)
            .pipe(takeUntil(this.destroy$))
            .subscribe(colors => {
                this.annotationColorUnsaved = colors?.unsavedLevel
                this.annotationColor = colors?.selectedLevel
            })

        this.store
            .select(selectDisplayedOnScatterPlot)
            .pipe(takeUntil(this.destroy$))
            .subscribe(selections => {
                if (selections.length === 0) {
                    this.colorBySelectionMembership = false
                    this.pointColorStrings = this.defaultPointColorStrings.map(colorString =>
                        d3.color(colorString)
                    )
                    return
                }
                this.colorBySelectionMembership = true
                const updatedPointColorStrings = [
                    '#575762',
                    '#000000',
                    ...[...selections]!.map((selection: any) => selection.annotationColor),
                ].map(colorString => d3.color(colorString))
                this.pointColorStrings = updatedPointColorStrings
            })

        this.actions$
            .pipe(takeUntil(this.destroy$), ofType(userActions.startLogout))
            .subscribe(() => {
                resetPlotData()
                this.plot?.destroy()
            })

        this.isLoadingData$ = this.store
            .select(selectIsLoadingPlotData)
            .pipe(takeUntil(this.destroy$))

        this.pointSize$ = this.store.select(selectPointSize)
        this.pointOpacity$ = this.store.select(selectPointOpacity)
        this.lassoType$ = this.store.select(selectLassoType)
    }

    ngAfterViewInit(): void {
        this.initializePlot(this.plotCanvas)
        if (hasDisplayPlotData()) {
            void this.drawPlot(getDisplayPlotData())
        }
        this.resizeObserver.observe(this.plotCanvas.nativeElement)
        this.store
            .select(selectHasMarkers)
            .pipe(
                filter(hasMarkers => hasMarkers),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                setTimeout(() => this.ensurePlotReadyAndDraw(), 0)
            })
        this.pointSize$.subscribe(size => {
            this.pointSize = size
            if (this.plot) {
                this.plot?.set({ pointSize: size })
            } else {
                this.initializePlot(this.plotCanvas)
            }
        })
        this.pointOpacity$.subscribe(opacity => {
            this.pointOpacity = opacity
            if (this.plot) {
                this.plot?.set({ opacity: opacity === 0 ? opacity : opacity / 10 })
            } else {
                this.initializePlot(this.plotCanvas)
            }
        })
        this.lassoType$.subscribe(type => {
            this.lassoType = type
            if (this.plot) {
                this.plot?.set({ lassoType: type } as any)
            } else {
                this.initializePlot(this.plotCanvas)
            }
        })
        setTimeout(() => this.ensurePlotReadyAndDraw(), 0)
        this.onResize()
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onResize())
        fromEvent(window, 'orientationchange')
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.onResize())
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
        this.resizeObserver.disconnect()
    }

    resetCamera(): void {
        this.plot?.zoomToOrigin()
    }

    eventHandler(e: Event): void {
        if (!(e instanceof TouchEvent)) return

        if (e.type === 'touchstart') {
            this.touchStartTime = Date.now()
            this.touchMoveAllowed = true
        }

        if (e.type === 'touchmove') {
            if (e.touches.length !== 2) {
                const timeSinceStart = Date.now() - (this.touchStartTime || 0)
                if (timeSinceStart < this.lassoLongPressTime) {
                    this.touchMoveAllowed = false
                    e.preventDefault()
                    return
                }
                if (!this.touchMoveAllowed) {
                    e.preventDefault()
                    return
                }
            }
            e.preventDefault()
        }

        if (e.touches.length === 2) {
            if (this.touchTimeout) {
                clearTimeout(this.touchTimeout)
                this.touchTimeout = null
            }

            e.preventDefault()
            const touch1 = e.touches[0]
            const touch2 = e.touches[1]
            const distance = Math.hypot(
                touch2.clientX - touch1.clientX,
                touch2.clientY - touch1.clientY
            )

            if (
                this.lastPinchDistance !== null &&
                Math.abs(distance - this.lastPinchDistance) >= 10
            ) {
                const zoomDistance: number = 20
                const deltaY = distance > this.lastPinchDistance ? -zoomDistance : zoomDistance

                const wheelEvent = new WheelEvent('wheel', {
                    deltaX: 0,
                    deltaY: deltaY,
                    deltaZ: 0,
                    deltaMode: 0,
                    clientX: (touch1.clientX + touch2.clientX) / 2,
                    clientY: (touch1.clientY + touch2.clientY) / 2,
                    screenX: (touch1.screenX + touch2.screenX) / 2,
                    screenY: (touch1.screenY + touch2.screenY) / 2,
                    bubbles: true,
                    cancelable: true,
                    composed: true,
                    view: window,
                })

                e.target?.dispatchEvent(wheelEvent)

                this.lastPinchDistance = distance
            } else if (this.lastPinchDistance === null) {
                this.lastPinchDistance = distance
            }
            return
        }

        if (e.touches.length === 0) {
            this.lastPinchDistance = null
            this.touchStartTime = null
            this.touchMoveAllowed = false
        }

        if (e.touches.length === 1) {
            if (this.touchTimeout) return //
            this.touchTimeout = window.setTimeout(() => {
                const touch = e.touches[0] || e.changedTouches[0]
                const type =
                    e.type === 'touchstart'
                        ? 'mousedown'
                        : e.type === 'touchend'
                        ? 'mouseup'
                        : 'mousemove'
                const mouseEvent = new MouseEvent(type, {
                    clientX: touch.clientX,
                    clientY: touch.clientY,
                    screenX: touch.screenX,
                    screenY: touch.screenY,
                    button: 0,
                    buttons: 1,
                    relatedTarget: null,
                    bubbles: true,
                    cancelable: true,
                    composed: true,
                    detail: 1,
                    view: e.view,
                })
                e.target?.dispatchEvent(mouseEvent)

                this.touchTimeout = null
            }, 30)
        }

        if (e.type === 'touchend' && e.changedTouches.length === 1) {
            // e.preventDefault()
            const touch = e.changedTouches[0]
            const mouseEvent = new MouseEvent('mouseup', {
                clientX: touch.clientX,
                clientY: touch.clientY,
                screenX: touch.screenX,
                screenY: touch.screenY,
                button: 0,
                buttons: 1,
                relatedTarget: e.target,
                bubbles: true,
                cancelable: true,
                composed: true,
            })
            e.target?.dispatchEvent(mouseEvent)
        }
    }

    private onResize(): void {
        const clientRect = this.plotCanvas.nativeElement.getBoundingClientRect()
        // wrapping the resizing within a timeout fixes a timing issue (not sure why)
        setTimeout(() => {
            d3.select(this.xAxisContainer.nativeElement)
                .attr('transform', `translate(0, ${clientRect.height})`)
                .call(this.xAxis)
            d3.select(this.yAxisContainer.nativeElement)
                .attr('transform', `translate(${clientRect.width}, 0)`)
                .call(this.yAxis)
            d3.select(this.colorAxisContainer.nativeElement).attr('transform', `translate(20, 50)`)
            d3.select(this.colorAxisContainer.nativeElement)
                .selectAll('.label')
                .style('font-size', '0.5rem')
                .style('fill', 'var(--app-color-neutral-60)')
        })
    }

    private ensurePlotReadyAndDraw(): void {
        if (!this.plot) {
            this.initializePlot(this.plotCanvas)
        }
        if (this.plot && hasDisplayPlotData()) {
            void this.drawPlot(getDisplayPlotData())
        }
    }

    private async drawPlot(data: PlotData, generation?: number): Promise<void> {
        if (!this.plot) {
            return
        }
        const gen = generation ?? ++this.drawGeneration

        if (!data.domainX || !data.domainY || data.points.length === 0) {
            if (gen !== this.drawGeneration) return
            this.clearPlotDisplay()
            this.store.dispatch(plotDataActions.plotDrawn())
            return
        }

        if (gen !== this.drawGeneration) return

        const segSources = [...new Set(data.points.map(p => p[5]).filter(Boolean))]
        console.log('[PlotData]', 'scatter plot draw started', {
            pointCount: data.points.length,
            segSources,
            domainX: data.domainX,
            domainY: data.domainY,
        })

        try {
            await this.drawPlotOnReadyPlot(data, gen)
        } catch (err) {
            if (gen !== this.drawGeneration) return
            console.error('Error drawing scatter plot:', err)
            this.store.dispatch(plotDataActions.plotDrawn())
        }
    }

    private clearPlotDisplay(): void {
        if (this.plot) {
            this.plot.clear()
            this.plot.reset()
        }
        this.segmentIds = new Uint32Array()
        this.segSources = []
        if (this.colorAxisContainer?.nativeElement) {
            d3.select(this.colorAxisContainer.nativeElement).selectAll('*').remove()
        }
    }

    private async drawPlotOnReadyPlot(data: PlotData, gen: number): Promise<void> {
        if (!this.plot) return
        if (gen !== this.drawGeneration) return

        const domainX = data.domainX
        const domainY = data.domainY
        if (!domainX || !domainY || data.points.length === 0) {
            this.clearPlotDisplay()
            return
        }

        // Yield so a deselect/clear can bump drawGeneration before we burn the main thread
        // on reverse/dedupe/sort of multi-million point arrays.
        await this.yieldForAbort(gen)
        if (gen !== this.drawGeneration) return

        // Copy before reverse — plotData$ may re-emit the same points array (e.g. selection
        // color updates); reversing in place would flip points on every redraw.
        const dedupedPoints = uniquePlotPoints(data.points.slice().reverse())
        if (gen !== this.drawGeneration) return
        console.log('[PlotData]', 'scatter plot draw → points deduped', {
            pointCount: dedupedPoints.length,
        })
        data = { ...data, points: dedupedPoints }
        let pointColorStrings = ['#575762', '#d5d5d5', '#a1a1a1'].map(colorString =>
            d3.color(colorString)
        )

        // Free selection: tint index 0 with the active annotation color.
        // Hierarchical membership mode: index 0 is background (not in any visible
        // selection) and must stay gray — otherwise send-down leftovers look selected.
        let pointColor: ColorMap = this.pointColorStrings.map((color, i) => {
            return i === 0 && this.annotationColor && !this.colorBySelectionMembership
                ? (this.annotationColor as Color)
                : color!.formatHex()
        })

        let pointColorActive: ColorMap = this.pointColorStrings.map((color, i) => {
            return i === 0
                ? this.annotationColorUnsaved || (this.annotationColorActive as Color)
                : color!.brighter().formatHex()
        }) as any
        let pointColorHover: ColorMap = this.pointColorStrings.map(color =>
            color!.brighter().formatHex()
        )

        let options: ScatterplotMethodOptions['draw'] = {
            zDataType: 'categorical',
        }
        let colorPointConversion = (color: number): number => {
            return color
        }

        if (data.domainColor) {
            await this.yieldForAbort(gen)
            if (gen !== this.drawGeneration) return
            const sortedValues = [...data.points]
                .map(
                    p =>
                        ((p[3] + 1) * data.domainColor?.upperBound! -
                            data.domainColor?.lowerBound!) /
                            2 +
                        data.domainColor?.lowerBound!
                )
                .sort((a, b) => a - b)
            if (gen !== this.drawGeneration) return
            const lowerPercentileIndex = Math.floor(0.01 * sortedValues.length)
            const upperPercentileIndex = Math.min(
                Math.ceil(0.99 * sortedValues.length),
                sortedValues.length - 1
            )
            const lowerBound = sortedValues[lowerPercentileIndex]
            const upperBound = sortedValues[upperPercentileIndex]
            this.zScale.domain([lowerBound, upperBound])
            const colorScale = this.zScale
                .copy()
                .domain([calcColorScale(lowerBound), calcColorScale(upperBound)])
            const viridisColors = Array.from({ length: 100 }, (_, i) => d3.color(colorScale(i))!)

            pointColor = viridisColors.map(color => color.formatHex())
            pointColorActive = viridisColors.map(color => color.brighter().formatHex())
            pointColorHover = viridisColors.map(color => color.darker().formatHex())

            options = { zDataType: 'continuous' }
            // color axis has to be normalized to 0..1 instead of -1..1
            colorPointConversion = (color: number) => {
                return (color + 1) / 2
            }
            if (data.legend) {
                data.points = [...data.points].map(p => [
                    p[0],
                    p[1],
                    p[2],
                    conditionalRound(
                        data.legend,
                        ((p[3] + 1) * data.domainColor?.upperBound! -
                            data.domainColor?.lowerBound!) /
                            2 +
                            data.domainColor?.lowerBound!
                    ),
                    p[4],
                    p[5],
                ])

                options = { zDataType: 'categorical' }
                const zlegendDomain = data.legend.map(l => l.value)
                // Use a local categorical palette. Legend keys are category indices, not
                // segment ids — getSegmentColors() is wrong here and can hang the draw.
                const palette = d3.schemeTableau10 ?? d3.schemeCategory10
                const zlegendColor = data.legend.map(
                    (_, i) => palette[i % palette.length]
                )
                const ordinal = d3.scaleOrdinal().domain(zlegendDomain).range(zlegendColor)
                d3.select(this.colorAxisContainer.nativeElement)
                    .attr('visibility', 'visible')
                    .call(
                        legendColor()
                            .shape('rect')
                            .shapeWidth(10)
                            .shapeHeight(10)
                            .labelOffset(5)
                            .labelFormat(d3.format(',.2r'))
                            .shapePadding(5)
                            .scale(ordinal) as any
                    )
                this.colorAxisContainer.nativeElement
                pointColorStrings = ['#ffffff', ...zlegendColor].map(
                    (
                        colorString //points with value 0 will be white
                    ) => d3.color(colorString)
                )
                pointColor = pointColorStrings.map(color => color!.formatHex())
                pointColorActive = pointColorStrings.map(color => color!.brighter().formatHex())
                pointColorHover = pointColorStrings.map(color => color!.brighter().formatHex())
            } else {
                d3.select(this.colorAxisContainer.nativeElement)
                    .attr('visibility', 'visible')
                    .call(this.zLegend as any)
            }
            this.colorAxisContainer.nativeElement
            function calcColorScale(bound: number) {
                return (
                    ((bound - data.domainColor?.lowerBound!) /
                        (data.domainColor?.upperBound! - data.domainColor?.lowerBound!)) *
                    100
                )
            }
        } else {
            d3.select(this.colorAxisContainer.nativeElement)
                .attr('visibility', 'hidden')
                .selectAll('*')
                .remove()
        }

        this.plot.set({
            colorBy: 'valueA',
            opacityBy: 'valueB',
            xScale: d3.scaleLinear().domain([domainX.lowerBound, domainX.upperBound]),
            yScale: d3.scaleLinear().domain([domainY.lowerBound, domainY.upperBound]),
            pointColor: pointColor,
            opacity: [1, 0.2],
            // library types are wrong
            pointColorActive: pointColorActive as any,
            pointColorHover: pointColorHover as any,
            // Selection requires holding Shift; plain LMB drag pans/zooms the plot.
            lassoInitiator: false,
            lassoOnLongPress: false,
        })

        // Do not clear()/reset() here: map toggles and selection updates re-emit plotData$ and
        // would blank the canvas for the whole (expensive) redraw. Keep the previous frame until
        // draw() replaces it. clearPlotDisplay() still runs when points are empty.

        if (gen !== this.drawGeneration) return

        this.segmentIds = new Uint32Array(data.points.map(point => point[0]))
        this.segSources = data.points.map(point => point[5])
        try {
            await this.yieldForAbort(gen)
            if (gen !== this.drawGeneration) return
            const drawPoints = data.points.map(point => [
                point[1],
                point[2],
                colorPointConversion(point[3]),
                point[4] || 0,
            ])
            if (gen !== this.drawGeneration) return
            await this.plot.draw(drawPoints, options)
            if (gen !== this.drawGeneration) return
            console.log('[PlotData]', 'scatter plot draw completed', {
                pointCount: data.points.length,
            })
            this.applySelectionHighlight(this.latestSelectionBuckets)
            this.store.dispatch(plotDataActions.plotDrawn())
        } catch (err) {
            if (gen !== this.drawGeneration) return
            console.error('Error in plot.draw:', err)
            this.store.dispatch(plotDataActions.plotDrawn())
        }

        function conditionalRound(legend: any, num: number) {
            const rounded = Math.round(num)
            return (
                (legend.findIndex((l: { key: number; value: number }) => l.key === rounded) + 1) *
                    2 || 0
            )
        }
    }

    /** Let input/deselect run; return once this draw has been superseded or after a macrotask. */
    private yieldForAbort(gen: number): Promise<void> {
        if (gen !== this.drawGeneration) return Promise.resolve()
        return new Promise(resolve => setTimeout(resolve, 0))
    }

    private applySelectionHighlight(buckets: SelectionBucket[]): void {
        if (!this.plot) return
        const pointIdxs: number[] = []
        for (const bucket of buckets) {
            if (bucket.points.size === 0) continue
            pointIdxs.push(...this.filteredSegmentIdsToPointIdxs(bucket.points, bucket.segSource))
        }
        this.plot.select(pointIdxs, { preventEvent: true })
    }

    private pointMatchesFilter(pointIdx: number): boolean {
        if (!this.filterPoints) return true
        const segSource = this.segSources[pointIdx]
        if (!segSource) return false
        return this.filterPoints.has(`${segSource}:${this.segmentIds[pointIdx]}`)
    }

    private setFilterPoints(filterPoints: Set<string> | null): void {
        this.filterPoints = filterPoints
    }

    private filteredSegmentIdsToPointIdxs(segmentIds: Set<number>, segSource?: string): number[] {
        let segmentIdsArray = [...segmentIds]
        if (this.filterPoints && segSource) {
            segmentIdsArray = segmentIdsArray.filter(id =>
                this.filterPoints?.has(`${segSource}:${id}`)
            )
        }
        return this.segmentIdsToPointIdxs(new Set(segmentIdsArray), segSource)
    }

    private segmentIdsToPointIdxs(segmentIds: Set<number>, segSource?: string): number[] {
        const result = []
        for (let i = 0; i < this.segmentIds.length; i++) {
            if (!segmentIds.has(this.segmentIds[i])) continue
            if (segSource && this.segSources[i] !== segSource) continue
            result.push(i)
        }
        return result
    }

    private initializePlot(container: ElementRef<HTMLCanvasElement>): void {
        if (this.plot) return
        this.plot = this.createPlotObject(container)
        if (this.pendingPlotData) {
            const pending = this.pendingPlotData
            this.pendingPlotData = null
            void this.drawPlot(pending)
        }
    }

    private createPlotObject(container: ElementRef): ScatterPlot {
        // todo: this function breaks if the plot panel is not visible on load
        const plot = createScatterplot({
            canvas: container.nativeElement,
            pointSize: this.pointSize,
            opacity: this.pointOpacity,
            lassoColor: '#e3443e',
            lassoType: this.lassoType,
            xScale: this.xScale,
            yScale: this.yScale,
        } as any)

        plot.subscribe(
            'select',
            // @ts-ignore: Typings for eventHandler from lib are incorrect
            (selection: { points: number[] }) => {
                let pointIdxs = selection.points
                if (this.filterPoints) {
                    pointIdxs = pointIdxs.filter(idx => this.pointMatchesFilter(idx))
                    // Sync highlight to the filtered set. Without this, a lasso of only
                    // out-of-level points leaves those points selected (no store update
                    // runs when the filtered set is empty, so applySelectionHighlight
                    // never corrects the plot).
                    this.plot?.select(pointIdxs, { preventEvent: true })
                }
                this.selectionUpdate$.next(pointIdxs)
            },
            0
        )

        plot.subscribe(
            'view',
            ({ xScale, yScale }) => {
                if (xScale != null) {
                    d3.select(this.xAxisContainer.nativeElement).call(this.xAxis.scale(xScale))
                }
                if (yScale != null) {
                    d3.select(this.yAxisContainer.nativeElement).call(this.yAxis.scale(yScale))
                }
                // Maintain legend position and visibility during view changes
                d3.select(this.colorAxisContainer.nativeElement).attr(
                    'transform',
                    `translate(20, 50)`
                )
                d3.select(this.colorAxisContainer.nativeElement)
                    .selectAll('.label')
                    .style('font-size', '0.5rem')
                    .style('fill', 'var(--app-color-neutral-60)')
            },
            0
        )

        return plot
    }

    preventDefault(e: MouseEvent): void {
        // This prevents dragging the lasso or panning beyond the canvas border
        //  from selecting text and other elements under the cursor
        e.preventDefault()
    }
}

type ScatterPlot = ReturnType<typeof createScatterplot>
